import { styled } from "@mui/material/styles";
import {
  Box,
  Paper,
  InputBase,
  Chip,
  Typography,
  Checkbox,
  Radio,
} from "@mui/material";
import { keyframes } from "@emotion/react";
import { alpha } from "@mui/material/styles";
import CheckIcon from "@mui/icons-material/Check";

const slideDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const DropdownContainer = styled(Box)(({ fullWidth }) => ({
  width: fullWidth ? "100%" : "auto",
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  position: "relative",
}));

export const Label = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: "11px",
  fontWeight: 500,
  lineHeight: "1.2",
  marginBottom: "2px",

  "& .required": {
    color: theme.palette.error.main,
    marginLeft: "2px",
    display: "inline-block",
  },
}));

export const DropdownHeader = styled("div", {
  shouldForwardProp: (prop) => !["isOpen", "disabled", "error", "dropdownWidth", "variant"].includes(prop),
})(
  ({ theme, isOpen, disabled, error, dropdownWidth, variant }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "4px 12px",
    minHeight: "32px",
    fontSize: "12px",
    borderRadius: "5px",
    backgroundColor:
      variant === "tableHeader"
        ? "#fff"
        : disabled
        ? "rgba(0, 0, 0, 0.05)"
        : theme.palette.background.paper,
    color: variant === "tableHeader" ? "#232323" : theme.palette.text.primary,
    border: `1px solid ${
      error
        ? theme.palette.error.main
        : isOpen
        ? theme.palette.primary.main
        : theme.palette.divider
    }`,
    boxSizing: "border-box",
    transition: theme.transitions.create([
      "border-color",
      "background-color",
      "box-shadow",
    ]),
    cursor: disabled ? "not-allowed" : "pointer",

    "& .truncated-text": {
      textDecoration: "none",
      display: "block",
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      maxWidth: dropdownWidth,
      color: variant === "tableHeader" ? "#232323" : "inherit",
    },

    "&:hover": {
      borderColor: !disabled && theme.palette.primary.main,
      backgroundColor: variant === "tableHeader" ? "#f5f5f5" : undefined,
    },

    "&.Mui-disabled": {
      backgroundColor:
        theme.palette.mode === "dark"
          ? alpha(theme.palette.action.disabled, 0.1)
          : alpha(theme.palette.action.disabled, 0.05),
      cursor: "not-allowed",
    },
  })
);

export const DropdownList = styled(Paper)(({ theme }) => ({
  position: "fixed",
  backgroundColor: theme.palette.background.paper,
  borderRadius: "4px",
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
  maxHeight: 200,
  overflowY: "auto",
  zIndex: 1300,
  padding: "2px",
  minWidth: 120,
  maxWidth: "calc(100vw - 32px)",
  width: "auto",
  display: "flex",
  flexDirection: "column",

  "&[data-popper-placement='bottom']": {
    animation: `${slideDown} 0.15s ease-out`,
    marginTop: 2,
  },

  "&[data-popper-placement='top']": {
    animation: `${slideUp} 0.15s ease-out`,
    marginBottom: 2,
  },

  "&::-webkit-scrollbar": {
    width: 4,
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    background: alpha(theme.palette.grey[500], 0.2),
    borderRadius: 2,
    "&:hover": {
      background: alpha(theme.palette.grey[500], 0.3),
    },
  },
}));

export const DropdownPlaceholder = styled(Box)(
  ({ theme, disabled, variant }) => ({
    color:
      variant === "tableHeader"
        ? "#666"
        : disabled
        ? theme.palette.text.disabled
        : theme.palette.text.secondary,
    fontSize: "12px",
    opacity: variant === "tableHeader" ? 1 : 0.7,
    lineHeight: "24px",
  })
);

export const SearchInput = styled(InputBase)(({ theme }) => ({
  width: "100%",
  padding: "2px 8px",
  height: "28px",
  fontSize: "12px",
  borderBottom: `1px solid ${theme.palette.divider}`,
  marginBottom: "2px",
  position: "sticky",
  top: 0,
  backgroundColor: theme.palette.background.paper,
  zIndex: 2,
  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",

  "&::before": {
    content: '""',
    position: "absolute",
    left: 0,
    right: 0,
    bottom: -2,
    height: 2,
    backgroundColor: theme.palette.background.paper,
  },

  "& input": {
    padding: "0",
    fontSize: "12px",
    "&::placeholder": {
      color: theme.palette.text.secondary,
      opacity: 0.7,
      fontSize: "11px",
    },
  },
}));

export const DropdownItemsContainer = styled("div")`
  flex: 1;
  overflow-y: auto;
  margin-top: 2px;
`;

export const DropdownItem = styled(Box)(({ theme, isSelected }) => ({
  padding: "8px",
  minHeight: "28px",
  fontSize: "12px",
  cursor: "pointer",
  display: "flex",
  alignItems: "flex-start",
  gap: "6px",
  borderRadius: "3px",
  color: isSelected ? "#3F49A7" : theme.palette.text.primary,
  backgroundColor: isSelected ? "rgba(63,73,167,0.06)" : "transparent",
  transition: theme.transitions.create(["background-color", "transform"], {
    duration: theme.transitions.duration.shortest,
  }),
  margin: "1px 2px",

  "&:hover": {
    backgroundColor: isSelected
      ? "rgba(63,73,167,0.08)"
      : "rgba(63,73,167,0.04)",
  },

  "& .selection-indicator": {
    width: 14,
    height: 14,
    flexShrink: 0,
    borderRadius: "50%",
    border: `1.5px solid ${isSelected ? "#3F49A7" : theme.palette.grey[400]}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: "2px",
    transition: theme.transitions.create(["border-color", "background-color"], {
      duration: theme.transitions.duration.shortest,
    }),
    backgroundColor: isSelected ? "#3F49A7" : "transparent",

    "& .MuiSvgIcon-root": {
      fontSize: 10,
      color: "#fff",
      opacity: isSelected ? 1 : 0,
      transform: isSelected ? "scale(1)" : "scale(0.5)",
      transition: theme.transitions.create(["opacity", "transform"], {
        duration: theme.transitions.duration.shortest,
      }),
    },
  },

  "& .item-label": {
    fontSize: "11px",
    fontWeight: isSelected ? 500 : 400,
    flex: 1,
    whiteSpace: "normal",
    wordBreak: "break-word",
    lineHeight: 1.4,
  },

  "&:hover .selection-indicator": {
    borderColor: "#3F49A7",
  },
}));

export const ChevronIcon = styled(Box, {
  shouldForwardProp: (prop) => !["isOpen", "disabled"].includes(prop),
})(({ theme, isOpen, disabled }) => ({
  width: 0,
  height: 0,
  borderStyle: "solid",
  borderWidth: isOpen ? "0 4px 4px 4px" : "4px 4px 0 4px",
  borderColor: isOpen
    ? `transparent transparent ${
        disabled ? theme.palette.text.disabled : theme.palette.text.secondary
      } transparent`
    : `${
        disabled ? theme.palette.text.disabled : theme.palette.text.secondary
      } transparent transparent transparent`,
  transition: theme.transitions.create("transform"),
  marginLeft: 8,
  opacity: disabled ? 0.5 : 1,
  alignSelf: "center",
}));

export const ErrorText = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: "12px",
  marginTop: "4px",
  lineHeight: 1.2,
}));

export const NoResults = styled(Box)(({ theme }) => ({
  padding: "12px",
  textAlign: "center",
  color: theme.palette.text.secondary,
  fontSize: "11px",
  fontWeight: 500,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "6px",
  margin: "2px",
}));

export const SelectedItemsList = styled("div")`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 2px;
`;

export const SelectedItem = styled(Chip)`
  height: 24px;
  font-size: 0.75rem;

  .MuiChip-label {
    padding-left: 8px;
    padding-right: 8px;
  }

  .MuiChip-deleteIcon {
    font-size: 16px;
    margin-right: 4px;
  }
`;

export const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
  padding: 0,
  width: 16,
  height: 16,
  marginRight: 8,
  color:
    theme.palette.mode === "dark"
      ? theme.palette.grey[600]
      : theme.palette.grey[400],

  "&.Mui-checked": {
    color: theme.palette.primary.main,
  },

  "& .MuiSvgIcon-root": {
    fontSize: 16,
  },

  "&:hover": {
    backgroundColor: "transparent",
  },
}));

export const StyledRadio = styled(Radio)(({ theme }) => ({
  padding: 0,
  width: 18,
  height: 18,
  marginRight: 8,
  color:
    theme.palette.mode === "dark"
      ? theme.palette.grey[600]
      : theme.palette.grey[400],

  "&.Mui-checked": {
    color: theme.palette.primary.main,
  },

  "& .MuiSvgIcon-root": {
    fontSize: 18,
  },

  "&:hover": {
    backgroundColor: "transparent",
  },
}));
